import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import type { IPopUpWidgetProps } from "@swiggy-private/dls-react-mxn/dist/WidgetController/widgets/PopUpWidget/types";
import { ApiResponse } from "@swiggy-private/js-utils/dist/fetch/types";
import type { ILocalCart } from "../types/cartTypes";

export interface IPopUpConfig {
    configInfo: Array<{
        card: IPopUpWidgetProps;
    }>;
}

export interface IApiConfigs {
    POPUP?: IPopUpConfig;
}

interface IBaseScreenState {
    data: ApiResponse<{ cards: IPLWidgetControllerCards[]; configs?: IApiConfigs }>;
    loading: boolean;
    error: boolean;
    lastUpdated: number;
}

export type AppliedFilters = {
    singleSelect: Record<string, string>;
    multiSelect: Record<string, string[]>;
};

interface IMenuLikeState extends IBaseScreenState {
    initialData: IPLWidgetControllerCards[];
    filteredData: IPLWidgetControllerCards[];
    parentType: string;
    categoryId: string;
    preference: string;
    emptyItemCard?: boolean;
    appliedFilters?: AppliedFilters;
    requestId?: string;
    configs?: IApiConfigs;
}

export interface ICrossSellEntry {
    itemId: string;
    itemName: string;
    isExpanded: boolean;
    cartItemIdsAtOpen?: string[];
}

export interface ITextPayload {
    info?: string;
    color?: string;
    font?: string;
}

export interface CrossSellConfig {
    "@type"?: string;
    crossSell?: {
        crossSellPerItem?: {
            crossSellMap?: Record<string, { rawChildItems?: Array<{ id: string }> }>;
        };
        crossSellConstraints?: {
            minItems?: number;
            maxItems?: number;
        };
    };
    title?: string;
    title_v2?: ITextPayload;
    description_v2?: ITextPayload;
    imageUrl?: string;
    frequencyCapping?: number;
}

export interface IReducerResponse {
    track: IBaseScreenState;
    placeOrderFailure: IBaseScreenState & {
        isFullScreenError: boolean;
    };
    menu: IMenuLikeState;
    billSummary: IBaseScreenState & {
        errorInfo: Record<string, string>;
    };
    cart: IBaseScreenState & {
        localCart: ILocalCart;
    };
    curtain: IBaseScreenState;
    pin: {
        data: Record<string, string>;
        loading: boolean;
        error: boolean;
        lastUpdated: number;
    };
    toast: {
        type: ToastType;
        message: string;
        show: boolean;
        autoDismiss?: boolean;
        autoDismissTime?: number;
    };
    paymentFailure: {
        show: boolean;
        amount: string;
        paymentFailureCardData?: {
            paymentIcon?: string;
            iconWidth?: number;
            iconHeight?: number;
            iconAlt?: string;
            title?: string;
            description?: string;
            buttonText?: string;
        };
    };
    collections: {
        [key: string]: IMenuLikeState & {
            id: string;
        };
    };
    crossSell: {
        entries: ICrossSellEntry[];
        config?: CrossSellConfig;
        shownItemIds: string[];
    };
}

export enum ToastType {
    ERROR = "ERROR",
    INFO = "INFO",
    SUCCESS_TICK = "SUCCESS_TICK",
    SUCCESS = "SUCCESS",
}

export interface IQrCodeAction {
    type: QrCodeActions;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload: Record<string, any>;
}

export enum QrCodeActions {
    TRACK_SCREEN_LOADING = "TRACK_SCREEN_LOADING",
    TRACK_SCREEN_SUCCESS = "TRACK_SCREEN_SUCCESS",
    TRACK_SCREEN_RESET = "TRACK_SCREEN_RESET",
    PLACE_ORDER_UPDATE = "PLACE_ORDER_UPDATE",
    TRACK_SCREEN_ERROR = "TRACK_SCREEN_ERROR",
    BILL_SUMMARY_LOADING = "BILL_SUMMARY_LOADING",
    BILL_SUMMARY_SUCCESS = "BILL_SUMMARY_SUCCESS",
    BILL_SUMMARY_ERROR = "BILL_SUMMARY_ERROR",
    MENU_SCREEN_LOADING = "MENU_SCREEN_LOADING",
    MENU_SCREEN_RESET = "MENU_SCREEN_RESET",
    MENU_SCREEN_SUCCESS = "MENU_SCREEN_SUCCESS",
    MENU_SCREEN_UPDATE_REQUEST_ID = "MENU_SCREEN_UPDATE_REQUEST_ID",
    MENU_SCREEN_UPDATE_CONFIGS = "MENU_SCREEN_UPDATE_CONFIGS",
    MENU_SCREEN_ERROR = "MENU_SCREEN_ERROR",
    CART_SCREEN_LOADING = "CART_SCREEN_LOADING",
    CART_SCREEN_SUCCESS = "CART_SCREEN_SUCCESS",
    CART_SCREEN_ERROR = "CART_SCREEN_ERROR",
    CART_UPDATE_LOADING = "CART_UPDATE_LOADING",
    CART_UPDATE_SUCCESS = "CART_UPDATE_SUCCESS",
    CART_UPDATE_ERROR = "CART_UPDATE_ERROR",
    UPDATE_LOCAL_CART = "UPDATE_LOCAL_CART",
    PLACE_ORDER_ERROR = "PLACE_ORDER_ERROR",
    PLACE_ORDER_RESET = "PLACE_ORDER_RESET",
    CLEAR_LOCAL_CART = "CLEAR_LOCAL_CART",
    CURTAIN_SCREEN_LOADING = "CURTAIN_SCREEN_LOADING",
    CURTAIN_SCREEN_SUCCESS = "CURTAIN_SCREEN_SUCCESS",
    CURTAIN_SCREEN_ERROR = "CURTAIN_SCREEN_ERROR",
    PIN_GENERATION_LOADING = "PIN_GENERATION_LOADING",
    PIN_GENERATION_RESET = "PIN_GENERATION_RESET",
    PIN_GENERATION_SUCCESS = "PIN_GENERATION_SUCCESS",
    PIN_GENERATION_FAIURE = "PIN_GENERATION_FAILURE",
    UPDATE_INITIAL_MENU = "UPDATE_INITIAL_MENU",
    UPDATE_FILTER_DATA = "UPDATE_FILTER_DATA",
    UPDATE_DATA_BY_PARENT_TYPE = "UPDATE_DATA_BY_PARENT_TYPE",
    UPDATE_DATA_BY_CATEGORY = "UPDATE_DATA_BY_CATEGORY",
    UPDATE_DATA_PREFERENCE = "UPDATE_DATA_PREFERENCE",

    COLLECTIONS_SCREEN_LOADING = "COLLECTIONS_SCREEN_LOADING",
    COLLECTIONS_SCREEN_SUCCESS = "COLLECTIONS_SCREEN_SUCCESS",
    COLLECTIONS_SCREEN_ERROR = "COLLECTIONS_SCREEN_ERROR",
    UPDATE_INITIAL_COLLECTIONS = "UPDATE_INITIAL_COLLECTIONS",
    UPDATE_COLLECTIONS_FILTER_DATA = "UPDATE_COLLECTIONS_FILTER_DATA",
    UPDATE_COLLECTIONS_BY_PARENT_TYPE = "UPDATE_COLLECTIONS_BY_PARENT_TYPE",
    UPDATE_COLLECTIONS_PREFERENCE = "UPDATE_COLLECTIONS_PREFERENCE",

    SHOW_TOAST = "SHOW_TOAST",
    DISMISS_TOAST = "DISMISS_TOAST",
    SHOW_PAYMENT_FAILURE = "SHOW_PAYMENT_FAILURE",
    HIDE_PAYMENT_FAILURE = "HIDE_PAYMENT_FAILURE",

    CROSS_SELL_ADD_ENTRY = "CROSS_SELL_ADD_ENTRY",
    CROSS_SELL_REMOVE_ENTRY = "CROSS_SELL_REMOVE_ENTRY",
    CROSS_SELL_CONFIG_LOADED = "CROSS_SELL_CONFIG_LOADED",
    CROSS_SELL_SET_SHOWN_ITEMS = "CROSS_SELL_SET_SHOWN_ITEMS",
}

export type {
    IChoices,
    IAddons,
    IVariation,
    IVariantGroups,
    IVariantsV2,
    ICartItems,
    ILocalCart,
} from "../types/cartTypes";
